import React from 'react';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {DestinationPicker} from '../DestinationPicker/DestinationPicker';
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import type {TFunction} from '@wix/wix-i18n-config';
import orderSummaryStyle from '../../OrderSummary.scss';
import {TotalUpdateSkeleton} from '../TotalUpdateSkeleton';
import {FreeShippingCoupon} from '../FreeShippingCoupon/FreeShippingCoupon';
import {SPECS} from '../../../../../../../domain/specs';

export enum TotalShippingDataHook {
  Root = 'TotalShipping.Root',
  TitleRoot = 'TotalShipping.TitleRoot',
  Title = 'TotalShipping.Title',
  Value = 'TotalShipping.Value',
  Picker = 'ShippingDestination.Picker',
  OriginalPrice = 'TotalShipping.OriginalPrice',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const TotalShipping = () => {
  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const ecomShowFreeShippingCouponPlacementInCart = experiments.enabled(
    SPECS.ecomShowFreeShippingCouponPlacementInCart
  );
  const {shouldDisplayDeliveryError, isPickup, hasShippingSubdivisionError} = useControllerProps().orderStore;
  const {cartSummary, isSummaryUpdating, couponAlignmentInCartAndCheckout} = useControllerProps().cartStore;
  const isFreeShippingCoupon = couponAlignmentInCartAndCheckout
    ? Boolean(cartSummary?.deliveryDiscounts.length)
    : cartSummary?.couponDiscount?.isFreeShippingCoupon;

  const shouldDisplayNoAmount = !cartSummary?.deliverySummary?.method;
  const originalDeliveryPrice = cartSummary?.deliverySummary?.price;
  const shouldShowStrikethroughPrice =
    ecomShowFreeShippingCouponPlacementInCart &&
    isFreeShippingCoupon &&
    (originalDeliveryPrice?.convertedAmount ?? 0) > 0;

  const getShippingRowValueText = (): string => {
    if (shouldDisplayNoAmount) {
      return t('cart.summary.shipping.noAmount');
    }
    if (cartSummary.priceSummary.delivery.convertedAmount === 0 || isFreeShippingCoupon) {
      return t('cart.free_shipping');
    }
    return cartSummary.priceSummary.delivery.formattedConvertedAmount;
  };

  const renderShippingValue = () => {
    if (isSummaryUpdating) {
      return <TotalUpdateSkeleton />;
    }

    const valueText = getShippingRowValueText();

    if (!shouldShowStrikethroughPrice) {
      return valueText;
    }

    return (
      <>
        <span
          data-hook={TotalShippingDataHook.OriginalPrice}
          data-wix-total-shipping-original-price={TotalShippingDataHook.OriginalPrice}
          className={orderSummaryStyle.originalPrice}
          aria-label={t('cart.orderSummary.originalShippingPrice.ariaLabel', {
            price: originalDeliveryPrice?.formattedConvertedAmount,
          })}>
          <del>{originalDeliveryPrice?.formattedConvertedAmount}</del>
        </span>
        <span
          data-wix-total-shipping-estimated-shipping={TotalShippingDataHook.Value}
          aria-label={t('cart.orderSummary.afterDiscountShippingPrice.ariaLabel', {price: valueText})}>
          {valueText}
        </span>
      </>
    );
  };

  const showPrice = !shouldDisplayDeliveryError;
  return (
    <div data-hook={TotalShippingDataHook.Root} className={orderSummaryStyle.totalShippingRoot}>
      <dl>
        <dt>
          <div data-hook={TotalShippingDataHook.TitleRoot}>
            <span data-hook={TotalShippingDataHook.Title}>{getTotalShippingTitle({t, isPickup})}</span>
          </div>
        </dt>

        {showPrice && (
          <dd
            data-hook={TotalShippingDataHook.Value}
            data-wix-total-shipping-estimated-shipping={
              shouldShowStrikethroughPrice ? undefined : TotalShippingDataHook.Value
            }
            className={shouldShowStrikethroughPrice ? orderSummaryStyle.deliveryValue : undefined}>
            {renderShippingValue()}
          </dd>
        )}
      </dl>
      <FreeShippingCoupon />
      <DestinationPicker dataHook={TotalShippingDataHook.Picker} hasPartialAddressError={hasShippingSubdivisionError} />
    </div>
  );
};

function getTotalShippingTitle({t, isPickup}: {t: TFunction; isPickup: boolean}) {
  return isPickup ? t('cart.estimated_pickup') : t('cart.estimated_shipping');
}
